import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { GatewayVpn, RegionVpn, VpnSession } from '../api';
import { sessionState } from '../format';

const TONE = {
  good: 'bg-sev-4/15 text-sev-4',
  waiting: 'bg-sev-3/20 text-foreground',
  bad: 'bg-sev-1/15 text-sev-1',
  quiet: 'bg-muted text-muted-foreground',
} as const;

function Pill({ tone, children, title }: { tone: keyof typeof TONE; children: string; title?: string }) {
  return (
    <Badge className={cn('border-0 whitespace-nowrap', TONE[tone])} title={title}>
      {children}
    </Badge>
  );
}

export function SessionStateBadge({ session }: { session: VpnSession }) {
  if (session.gatewayRefused && !session.endedAt) {
    return (
      <Pill tone="bad" title={session.gatewayRefused}>
        Refused by the gateway
      </Pill>
    );
  }
  const state = sessionState(session);
  if (state === 'live') return <Pill tone="good">Live</Pill>;
  if (state === 'connecting') {
    return (
      <Pill tone="waiting" title="The client hasn't reached the gateway yet">
        Connecting
      </Pill>
    );
  }
  return <Pill tone="quiet">Ended</Pill>;
}

/** The VPN in a region in one word: its setting against what its gateways say. */
export function RegionVpnBadge({ region }: { region: RegionVpn }) {
  if (!region.enabled) return <Pill tone="quiet">Off</Pill>;
  const running = region.gateways.filter((g) => g.vpn.report?.on).length;
  if (running > 0) {
    return (
      <Pill tone="good" title={`${String(running)} of ${String(region.gateways.length)} gateways running it`}>
        On
      </Pill>
    );
  }
  if (region.gateways.some((g) => g.vpn.available)) {
    return (
      <Pill tone="waiting" title="No gateway has said it's on yet">
        Turning on
      </Pill>
    );
  }
  return (
    <Pill tone="bad" title="No gateway here can run it now">
      Unavailable
    </Pill>
  );
}

/** The VPN at a gateway in one word: its region's setting against what the gateway says. */
export function GatewayVpnBadge({ vpn }: { vpn: GatewayVpn }) {
  if (!vpn.enabled) return <Pill tone="quiet">Off</Pill>;
  if (vpn.report?.error) {
    return (
      <Pill tone="bad" title={vpn.report.error}>
        Error
      </Pill>
    );
  }
  if (!vpn.available) {
    return (
      <Pill tone="bad" title={vpn.unavailableReason ?? undefined}>
        Unavailable
      </Pill>
    );
  }
  if (!vpn.report?.on) {
    return (
      <Pill tone="waiting" title="The gateway hasn't said it's on yet">
        Turning on
      </Pill>
    );
  }
  return <Pill tone="good">On</Pill>;
}
